import { useMemo, useEffect, useCallback, useState } from 'react';
import axios from 'axios';
import { useGetRunLegLanesLazyQuery } from '../queries/getRunLegLanes.query.generated';
import { useUser } from '@store';

interface StopLeg {
  fromLocationId: number | null;
  toLocationId: number | null;
}

/**
 * Fetches lane route_polylines for consecutive stop pairs in a run.
 * For any missing lanes, calls the lane shell endpoint to create them,
 * then refetches to pick up the polyline once enriched.
 *
 * Returns an array of encoded polyline strings (or null) aligned to legs:
 *   legPolylines[i] = polyline from stop[i] to stop[i+1]
 */
export function useRunLegPolylines(
  customerId: number | undefined,
  legs: StopLeg[],
) {
  const user = useUser();
  const [getLanes, { data, loading }] = useGetRunLegLanesLazyQuery();
  const [creatingLanes, setCreatingLanes] = useState(false);

  // Build the OR filter for all leg pairs that have location IDs
  const validLegs = useMemo(
    () => legs.filter(l => l.fromLocationId && l.toLocationId),
    [legs],
  );

  // Fetch lanes for all valid legs
  const fetchLanes = useCallback(() => {
    if (!customerId || validLegs.length === 0) return;

    const legFilters = validLegs.map(l => ({
      origin_location_id: { _eq: l.fromLocationId },
      destination_location_id: { _eq: l.toLocationId },
    }));

    getLanes({
      variables: { customerId, legs: legFilters },
      fetchPolicy: 'network-only',
    });
  }, [customerId, validLegs, getLanes]);

  useEffect(() => {
    fetchLanes();
  }, [fetchLanes]);

  // Map fetched lanes by origin→destination key
  const laneMap = useMemo(() => {
    const map = new Map<string, string | null>();
    data?.lanes?.forEach(lane => {
      const key = `${lane.origin_location_id}-${lane.destination_location_id}`;
      map.set(key, lane.route_polyline || null);
    });
    return map;
  }, [data]);

  // Identify missing lanes and create shells
  useEffect(() => {
    if (!customerId || loading || creatingLanes) return;

    const missing = validLegs.filter(l => {
      const key = `${l.fromLocationId}-${l.toLocationId}`;
      return !laneMap.has(key);
    });

    if (missing.length === 0) return;

    const createMissingLanes = async () => {
      setCreatingLanes(true);
      try {
        const token = await user?.getToken();
        if (!token) return;

        const baseUrl = window.location.origin;

        // Create lane shells for each missing pair
        await Promise.allSettled(
          missing.map(l =>
            axios.post(
              `${baseUrl}/.netlify/functions/handleLaneShellPair`,
              {
                variables: {
                  customerId,
                  pickupId: l.fromLocationId,
                  deliveryId: l.toLocationId,
                  pickup: { name: '' },
                  delivery: { name: '' },
                },
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              },
            ),
          ),
        );

        // Refetch after a short delay to allow enrichment to start
        setTimeout(() => {
          fetchLanes();
          setCreatingLanes(false);
        }, 3000);
      } catch (err) {
        console.error('Failed to create lane shells:', err);
        setCreatingLanes(false);
      }
    };

    createMissingLanes();
  }, [customerId, validLegs, laneMap, loading, creatingLanes, user, fetchLanes]);

  // Build the final polyline array aligned to the legs input
  const legPolylines: (string | null)[] = useMemo(() => {
    return legs.map(l => {
      if (!l.fromLocationId || !l.toLocationId) return null;
      const key = `${l.fromLocationId}-${l.toLocationId}`;
      return laneMap.get(key) ?? null;
    });
  }, [legs, laneMap]);

  return { legPolylines, loading: loading || creatingLanes };
}
